import React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useParams } from 'react-router-dom';
import { formatDocument, cleanDocument } from '../utils/formatters';
import { useDossieService } from '../../../../../../use-cases/dossie';

export const OrigemConsultaWidget: React.FC = () => {
  const {
    origemConsulta,
    origemMinimizada,
    toggleOrigemMinimizada,
    voltarParaOrigem,
    fecharOrigemConsulta,
  } = useDossieService();

  const { documento: documentoAtual } = useParams<{ documento: string }>();

  if (!origemConsulta) return null;

  const cleanDocAtual = cleanDocument(documentoAtual);
  const cleanDocOrigem = cleanDocument(origemConsulta.documento);

  if (cleanDocAtual && cleanDocOrigem && cleanDocAtual === cleanDocOrigem) {
    return null;
  }

  const isOrigemPF = origemConsulta.tipo === 'CPF';
  const docFormatado = formatDocument(origemConsulta.documento, isOrigemPF);

  if (origemMinimizada) {
    return (
      <Paper
        elevation={6}
        className="no-print"
        sx={{
          position: 'fixed',
          bottom: { xs: 16, md: 24 },
          right: { xs: 16, md: 24 },
          zIndex: 1200,
          borderRadius: '30px',
          backgroundColor: '#ffffff',
          border: '1.5px solid #ece7f2',
          boxShadow: '0 8px 24px rgba(114, 57, 137, 0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.8,
          transition: 'all 0.25s ease',
          '&:hover': {
            borderColor: '#723989',
            boxShadow: '0 10px 28px rgba(114, 57, 137, 0.28)',
          },
        }}
      >
        <Tooltip title="Clique para expandir" arrow placement="top">
          <Box
            onClick={toggleOrigemMinimizada}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: '#f3eff7',
                color: '#723989',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isOrigemPF ? (
                <PersonOutlineOutlinedIcon sx={{ fontSize: '1.1rem' }} />
              ) : (
                <BusinessOutlinedIcon sx={{ fontSize: '1.1rem' }} />
              )}
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: '#64748b',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  lineHeight: 1.1,
                }}
              >
                Origem: {origemConsulta.tipo}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#1e293b',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  lineHeight: 1.2,
                }}
              >
                {docFormatado}
              </Typography>
            </Box>
          </Box>
        </Tooltip>

        <Tooltip title={`Voltar para ${origemConsulta.tipo}`} arrow placement="top">
          <IconButton
            size="small"
            onClick={voltarParaOrigem}
            sx={{
              color: '#ffffff',
              backgroundColor: '#723989',
              p: 0.7,
              '&:hover': { backgroundColor: '#562768' },
            }}
          >
            <SwapHorizOutlinedIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Expandir" arrow placement="top">
          <IconButton size="small" onClick={toggleOrigemMinimizada} sx={{ color: '#64748b', p: 0.5 }}>
            <KeyboardArrowUpOutlinedIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Fechar" arrow placement="top">
          <IconButton
            size="small"
            onClick={fecharOrigemConsulta}
            sx={{
              color: '#94a3b8',
              p: 0.5,
              '&:hover': { color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.08)' },
            }}
          >
            <CloseOutlinedIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Tooltip>
      </Paper>
    );
  }

  // Modo Expandido (Card Flutuante Picture-in-Picture)
  return (
    <Paper
      elevation={8}
      className="no-print"
      sx={{
        position: 'fixed',
        bottom: { xs: 16, md: 24 },
        right: { xs: 16, md: 24 },
        width: { xs: 'calc(100vw - 32px)', sm: 340 },
        maxWidth: 360,
        zIndex: 1200,
        borderRadius: '18px',
        backgroundColor: '#ffffff',
        border: '1.5px solid #ece7f2',
        boxShadow: '0 12px 36px rgba(114, 57, 137, 0.18)',
        p: 2,
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Cabeçalho */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              backgroundColor: '#f3eff7',
              color: '#723989',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isOrigemPF ? (
              <PersonOutlineOutlinedIcon sx={{ fontSize: '1.15rem' }} />
            ) : (
              <BusinessOutlinedIcon sx={{ fontSize: '1.15rem' }} />
            )}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', lineHeight: 1 }}>
              Consulta de Origem
            </Typography>
            <Typography variant="subtitle2" sx={{ color: '#1e293b', fontWeight: 800, lineHeight: 1.3 }}>
              Vindo de {origemConsulta.tipo}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={0.5}>
          <Chip
            label={origemConsulta.tipo}
            size="small"
            sx={{
              backgroundColor: '#f3eff7',
              color: '#723989',
              fontWeight: 800,
              fontSize: '0.7rem',
              height: 22,
            }}
          />
          <Tooltip title="Minimizar" arrow placement="top">
            <IconButton size="small" onClick={toggleOrigemMinimizada} sx={{ color: '#64748b', p: 0.6 }}>
              <RemoveOutlinedIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Fechar" arrow placement="top">
            <IconButton
              size="small"
              onClick={fecharOrigemConsulta}
              sx={{
                color: '#94a3b8',
                p: 0.6,
                '&:hover': { color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.08)' },
              }}
            >
              <CloseOutlinedIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Dados do Documento de Origem */}
      <Box
        sx={{
          backgroundColor: '#faf8fc',
          borderRadius: '12px',
          border: '1px solid #ece7f2',
          p: 1.5,
          mb: 2,
        }}
      >
        {origemConsulta.nome && (
          <Tooltip title={origemConsulta.nome} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                color: '#1e293b',
                fontWeight: 700,
                mb: 0.5,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {origemConsulta.nome}
            </Typography>
          </Tooltip>
        )}

        <Typography
          variant="caption"
          sx={{
            color: '#723989',
            fontWeight: 800,
            fontSize: '0.85rem',
            letterSpacing: '0.02em',
            display: 'block',
          }}
        >
          {docFormatado}
        </Typography>

        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem', display: 'block', mt: 0.5 }}>
          Clique abaixo para alternar de volta.
        </Typography>
      </Box>

      {/* Botão de Alternância / Voltar */}
      <Button
        variant="contained"
        fullWidth
        onClick={voltarParaOrigem}
        startIcon={<ArrowBackOutlinedIcon sx={{ fontSize: '1.05rem' }} />}
        sx={{
          backgroundColor: '#723989',
          color: '#ffffff',
          fontWeight: 700,
          textTransform: 'none',
          borderRadius: '10px',
          py: 0.9,
          fontSize: '0.86rem',
          boxShadow: '0 4px 14px rgba(114, 57, 137, 0.25)',
          '&:hover': {
            backgroundColor: '#562768',
            boxShadow: '0 6px 18px rgba(114, 57, 137, 0.35)',
          },
        }}
      >
        Voltar para {origemConsulta.tipo}
      </Button>
    </Paper>
  );
};
